// ======================================================================
// 📁 File: src/modules/ticket/pages/TicketList.jsx
// 📝 Purpose:
//   - Ticket listing (All / My / Drill-down)
//   - Row click opens Ticket Detail Drawer
// 🕒 Updated: 2025-12-26
// ======================================================================

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosClient from "../../../api/axiosClient";

export default function TicketList({ filter = "all", context = null }) {
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [slaDetail, setSlaDetail] = useState(null);

  // ------------------------------------------------------------------
  // Query params (FIXED: FULL CONTEXT SUPPORT)
  // ------------------------------------------------------------------
  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: pageSize,
    };

    if (search) params.q = search;

    // My tickets
    if (filter === "my") {
      params.assigned_to_id = "me";
    }

    // -----------------------------
    // Drill-down context handling
    // -----------------------------
    if (context) {
      // Location drill-down
      if (context.type === "location" && context.id) {
        params.location_id = context.id;
      }

      // Executive drill-down
      if (context.type === "executive" && context.id) {
        params.assigned_to_id = context.id;
      }

      // KPI drill-down
      if (context.type === "kpi") {
        switch (context.key) {
          case "tickets_closed":
            params.status = "CLOSED";
            break;

          case "tickets_created":
            // no extra filter needed
            break;

          case "sla_breached":
            params.sla_status = "BREACHED";
            break;

          case "sla_met":
            params.sla_status = "MET";
            break;

          case "escalation_ratio":
            params.escalation_level = "GT_0";
            break;

          default:
            break;
        }
      }
    }

    return params;
  }, [filter, context, search, page, pageSize]);

  // ------------------------------------------------------------------
  // Load ticket list
  // ------------------------------------------------------------------
  useEffect(() => {
    setLoading(true);
    axiosClient
      .get("/ticket-management/tickets", { params: queryParams })
      .then((res) => {
        if (res.data?.success) {
          setTickets(res.data.data.items || []);
          setTotal(res.data.data.total || 0);
        }
      })
      .finally(() => setLoading(false));
  }, [queryParams]);

  // ------------------------------------------------------------------
  // Open drawer → load ticket + SLA
  // ------------------------------------------------------------------
  const openTicketDrawer = (ticketId) => {
    setDrawerOpen(true);
    setSelectedTicketId(ticketId);
    setTicketDetail(null);
    setSlaDetail(null);

    axiosClient
      .get(`/ticket-management/tickets/${ticketId}`)
      .then((res) => {
        if (res.data?.success) setTicketDetail(res.data.data);
      });

    axiosClient
      .get(`/ticket-management/tickets/${ticketId}/sla`)
      .then((res) => {
        if (res.data?.success) setSlaDetail(res.data.data);
      });
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedTicketId(null);
  };

  const totalPages = Math.ceil(total / pageSize);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div className="ticket-list">
      <h4 className="fw-bold mb-3">🎫 Tickets</h4>

      <input
        className="form-control mb-3"
        placeholder="Search tickets..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
      />

      <div className="card shadow-sm border-0">
        <table className="table mb-0">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Assigned</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr
                key={t.id}
                style={{ cursor: "pointer" }}
                onClick={() => openTicketDrawer(t.id)}
              >
                <td>{t.ticket_number || t.id}</td>
                <td>{t.title}</td>
                <td>{t.status}</td>
                <td>{t.priority}</td>
                <td>{t.assigned_to_name || "-"}</td>
                <td>{new Date(t.created_at).toLocaleString()}</td>
              </tr>
            ))}

            {tickets.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center py-4 text-muted">
                  No tickets found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between mt-3">
          <span>
            Page {page} of {totalPages}
          </span>
          <div>
            <button disabled={page === 1} onClick={() => setPage(page - 1)}>
              Prev
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ---------------- TICKET DETAIL DRAWER ---------------- */}
      {drawerOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "420px",
            height: "100vh",
            background: "#fff",
            boxShadow: "-6px 0 16px rgba(0,0,0,0.15)",
            zIndex: 9999,
            padding: 20,
            overflowY: "auto",
          }}
        >
          <button onClick={closeDrawer} style={{ float: "right" }}>
            ✕
          </button>
          <h4>Ticket Details</h4>

          {!ticketDetail && <p>Loading...</p>}

          {ticketDetail && (
            <>
              <p>
                <strong>Title:</strong> {ticketDetail.title}
              </p>
              <p>
                <strong>Status:</strong> {ticketDetail.status}
              </p>
              <p>
                <strong>Priority:</strong> {ticketDetail.priority}
              </p>
              <p>
                <strong>Assigned To:</strong>{" "}
                {ticketDetail.assigned_to?.name || "-"}
              </p>
              <p>
                <strong>Created:</strong>{" "}
                {new Date(ticketDetail.created_at).toLocaleString()}
              </p>
            </>
          )}

          {slaDetail && slaDetail.applied && (
            <>
              <hr />
              <h5>SLA</h5>
              <p>
                <strong>Status:</strong> {slaDetail.sla_status}
              </p>
              <p>
                <strong>Escalation:</strong> Level {slaDetail.escalation_level}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
